import '@assets/styles/tiptap-preview.css';

import { handleApiResponse } from '@src/api/apiClient/axiosClient';
import {
  ChannelMember,
  channelMemberApiClient,
} from '@src/api/shared/community/channelMemberApiClient';
import {
  Chat,
  chatApiClient,
  ChatGroup,
  ChatReportErrorResponse,
  ChatReportRequest,
  ChatReportResponse,
  PinnedChat,
} from '@src/api/shared/community/chatApiClient';
import IconImage from '@src/assets/images/common/icon/chat-action/icon-image.svg';
import { useSharedAuth } from '@src/context/auth/useSharedAuth';
import { useChatEditing } from '@src/context/ChatEditingContext';
import { ChannelMemberUserTypeEnum } from '@src/enum/ChannelMemberUserTypeEnum';
import LoginRecommendOverlay from '@src/features/shared/community/components/chat/LoginRecommendOverlay';
import { LinkPreviewData } from '@src/features/shared/community/components/chat/PreviewOgp';
import ReportModal from '@src/features/shared/community/components/chat/ReportModal';
import { ConfirmHidePreviewModal } from '@src/features/shared/community/components/ConfirmHidePreviewModal';
import { ConfirmMessageDeleteModal } from '@src/features/shared/community/components/ConfirmMessageDeleteModal';
import { ChatReportReasonEnum } from '@src/features/shared/community/enums/chatReportReason';
import { useToast } from '@src/features/toast/context/toastContext';
import { useChatDelete } from '@src/hooks/useChatDelete';
import { useChatScrollManager } from '@src/hooks/useChatScrollManager';
import { useDebouncedEffect } from '@src/hooks/useDebouncedEffect';
import { useIOSKeyboard } from '@src/hooks/useIOSKeyboard';
import { useIsMobile } from '@src/hooks/useIsMobile';
import { ScrollPriority } from '@src/hooks/useScrollActionManager';
import TOAST_MESSAGE from '@src/modules/constants/toastMessage';
import { RootState } from '@src/store/store.ts';
import { TiptapEditorHandle } from '@src/ui/Editor';
import { CommunityTiptapEditor } from '@src/ui/templates/Shared/CommunityTiptapEditor/CommunityTiptapEditor';
import { hasFiles } from '@src/utils/dragDropUtils';
import { ERROR_MESSAGES } from '@src/utils/errorMessages.ts';
import { Attachment, deleteChatDraft, loadChatDraft, saveChatDraft } from '@src/utils/idb';
import logger from '@src/utils/logger';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';

import ChatMessage, { ChatMenuPermission } from './chat/ChatMessage';
import LoginPrompt from './LoginPrompt';

type ChatBoxProps = {
  channelCode: string;
  channelName: string;
  channelVisibility?: number;
  canPost: boolean;
  allowFileAttachment: boolean;
  allowAnonymousPost: boolean;
  isLoggedIn: boolean;
  chatGroups: ChatGroup[];
  fetchMoreChats: () => Promise<void>;
  fetchMoreNewChats?: () => Promise<void>;
  fetchMoreNewerChats?: () => Promise<void>; // for infinite scroll down
  hasMoreChats: boolean;
  hasMoreNewChats?: boolean;
  hasMoreNewerChats?: boolean; // for infinite scroll down
  pinnedChat?: PinnedChat;
  onPinClick: (chat_id: number | undefined) => Promise<void>;

  onSend: () => void;
  mutateChats: () => void;
  contentHeight?: string;
  footerRef?: React.RefObject<HTMLDivElement>;
  onFooterHeightChange?: () => void;
  unreadCount?: number;
  firstUnreadChatId?: number | null;
  hasUnread?: boolean;
  initialChatId?: number;
  setInitialChatId?: (chatId?: number) => void;
};
export type ChatBoxHandle = {
  scrollToChatById: (chatId: number) => void;
  scrollToUnreadOrBottom: (firstUnreadChatId?: number | null) => void;
  editPinnedChat: (pinnedChat: PinnedChat) => void;
  deletePinnedChat: (pinnedChat: PinnedChat) => void;
  reportPinnedChat: (pinnedChat: PinnedChat) => void;
  getChatMenuPermission: (chat: Chat) => ChatMenuPermission;
};

const decodeHtmlEntities = (text: string): string => {
  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  return textarea.value;
};

const extractFirstUrl = (text: string): string | null => {
  const withoutMentions = text.replace(
    /<a[^>]*class=["'][^"']*mention[^"']*["'][^>]*>.*?<\/a>/gi,
    '',
  );
  const match = withoutMentions.match(
    /<a\b[^>]*href=(["'])(https?:\/\/[^\s"'<>]+(?=$|[\s"'<>、]))\1[^>]*>/i,
  );
  if (!match) {
    return null;
  }
  const rawUrl = match[2];
  const cleanedUrl = rawUrl.replace(/['""'\]!?,.]+$/, '');
  return decodeHtmlEntities(cleanedUrl);
};

const fetchLinkPreview = async (url: string): Promise<LinkPreviewData | null> => {
  try {
    const res = await chatApiClient.linkPreview(url);

    // Auto-hide preview if insufficient data (no title and no description)
    const hasTitle = res.title && res.title.trim().length > 0;
    const hasDescription = res.description && res.description.trim().length > 0;

    if (!hasTitle && !hasDescription) {
      return null;
    }

    return {
      url: res.url,
      siteName: res.siteName || new URL(url).hostname,
      title: res.title,
      description: res.description,
      image: res.image,
    };
  } catch {
    // Don't show preview if API fails
    return null;
  }
};

const SCROLL_CONFIG = {
  DELAY_MS: 200,
  DEBOUNCE_MS: 50,
  THROTTLE_MS: 16, // ~60fps
  NEW_LINE_OFFSET: 60,
  NEAR_BOTTOM_THRESHOLD: 50,
  SCROLL_VERIFICATION_THRESHOLD: 10,
  MAX_VERIFICATION_ATTEMPTS: 2,
  FOOTER_ADJUSTMENT_THRESHOLD: 5,
  UNREAD_VISIBILITY_THRESHOLD: 100,
} as const;

const TIMING_CONFIG = {
  INFINITE_SCROLL_BUFFER: 350, // 300ms blockDuration + 50ms buffer
  SAFETY_TIMEOUT: 10000,
  BATCH_DELAY: 100,
  SEND_SCROLL_DELAY_MULTIPLE: 25,
  SEND_SCROLL_DELAY_SINGLE: 50,
  INFINITE_SCROLL_DOWN_RESET: 1000,
  SCROLL_RESET_DELAY: 50,
  VISIBILITY_RESET_DELAY: 100,
  DRAG_RESET_DELAY: 100,
  UNREAD_LINE_RESET_DELAY: 300,
  SCROLL_RESTORATION_DELAY: 0,
  SMOOTH_SCROLL_DURATION: 0,
} as const;

const LINK_PREVIEW_CONFIG = {
  MAX_CONCURRENT_REQUESTS: 3,
  BATCH_DELAY_MS: 100,
} as const;

// Helper functions for scroll calculations
const calculateScrollToUnreadPosition = (
  element: HTMLDivElement,
  container: HTMLDivElement,
): number => {
  const containerRect = container.getBoundingClientRect();
  const elementRect = element.getBoundingClientRect();
  const newLineOffset = SCROLL_CONFIG.NEW_LINE_OFFSET;
  return container.scrollTop + (elementRect.top - containerRect.top) - newLineOffset;
};

const cancelScrollActions = (
  scrollManager: ReturnType<typeof useChatScrollManager>['scrollManager'],
  firstUnreadChatId: number,
): void => {
  scrollManager.cancelAction(`initialChatUnreadScroll-${firstUnreadChatId}`);
  scrollManager.cancelAction(`initialChatScroll-${firstUnreadChatId}`);
};

const createScrollToUnreadAction = (
  element: HTMLDivElement,
  container: HTMLDivElement,
): (() => void) => {
  return () => {
    const targetScrollTop = calculateScrollToUnreadPosition(element, container);
    container.scrollTo({ top: Math.max(0, targetScrollTop), behavior: 'smooth' });
  };
};

const ChatBox = forwardRef<ChatBoxHandle, ChatBoxProps>(
  (
    {
      channelCode,
      channelName,
      channelVisibility,
      canPost,
      allowFileAttachment,
      allowAnonymousPost,
      isLoggedIn,
      chatGroups,
      fetchMoreChats,
      fetchMoreNewerChats,
      hasMoreChats,
      hasMoreNewerChats,
      pinnedChat,
      onPinClick,

      onSend,
      mutateChats,
      footerRef,
      onFooterHeightChange,
      firstUnreadChatId,
      initialChatId,
    },
    ref,
  ) => {
    const [channelMembers, setChannelMembers] = useState<ChannelMember[]>([]);
    const chatContainerRef = useRef<HTMLDivElement>(null);
    const tiptapEditorRef = useRef<TiptapEditorHandle>(null);
    const [creatingMessage, setCreatingMessage] = useState<string>('');
    const [creatingAttachments, setCreatingAttachments] = useState<Attachment[]>([]);
    const { editingChatId, setEditingChatId } = useChatEditing();
    const [editingMessage, setEditingMessage] = useState<string>('');
    const [editingAttachments, setEditingAttachments] = useState<Attachment[]>([]);
    const [isAnonymous, setIsAnonymous] = useState<boolean>(false);
    const [linkPreviewCache, setLinkPreviewCache] = useState<
      Record<string, LinkPreviewData | null>
    >({});
    const [isActionMenuSpOpen, setIsActionMenuSpOpen] = useState<boolean>(false);
    const [currentChatIdSp, setCurrentChatIdSp] = useState<number>();
    const isLoadingMoreChats = useRef<boolean>(false);
    const [showLoadingIndicator, setShowLoadingIndicator] = useState<boolean>(false);
    const isLoadingNewerChats = useRef<boolean>(false);
    const prevChatCountBeforeFetch = useRef<number>(0);
    const isInitialRender = useRef<boolean>(true);
    const prevChannelCode = useRef<string | undefined>();
    const chatRefs = useRef<Record<number, HTMLDivElement | null>>({});

    const allChats = useMemo(() => chatGroups.flatMap((g) => g.chats), [chatGroups]);

    const totalChatCount = useMemo(() => allChats.length, [allChats]);

    const newestChat = useMemo(() => allChats[allChats.length - 1], [allChats]);

    const { profile } = useSharedAuth();
    const currentUserType = profile?.isTenant
      ? ChannelMemberUserTypeEnum.TENANT_USER
      : ChannelMemberUserTypeEnum.COMPANY_USER;
    const currentUserId = profile?.isTenant
      ? profile?.tenantProfile?.user?.id
      : profile?.companyProfile?.user?.id;

    const hasUserScrolledUpRef = useRef<boolean>(false);

    const {
      isNearBottom,
      updateWasAtBottom,
      scrollToBottom,
      rememberScrollHeight,
      restoreScrollHeight,
      scrollManager,
    } = useChatScrollManager({ chatContainerRef, chatRefs, hasUserScrolledUpRef });
    const wasAtBottomRef = useRef<boolean>(true);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [deletingChatId, setDeletingChatId] = useState<number | null>(null);
    const [isReportModalOpen, setIsReportModalOpen] = useState(false);
    const [reportingChatId, setReportingChatId] = useState<number | null>(null);
    const [isHidePreviewModalOpen, setIsHidePreviewModalOpen] = useState(false);
    const [hidingPreviewChatId, setHidingPreviewChatId] = useState<number | null>(null);
    const { deleteMessage, isDeleting } = useChatDelete(channelCode);
    const toast = useToast();
    const [isDragging, setIsDragging] = useState(false);
    const dragCounter = useRef(0);
    const [, forceUpdate] = useReducer((x) => x + 1, 0);
    const prevScrollToId = useRef<number | null>(null);
    const isFileDownloading = useSelector(
      (state: RootState) => state.fileDownload.isFileDownloading,
    );

    const isInfiniteScrollDownRef = useRef<boolean>(false);
    const isSendingRef = useRef<boolean>(false);
    const shouldAutoScrollAfterFooter = useRef<boolean>(false);

    const [viewportHeight, setViewportHeight] = useState(
      window.visualViewport?.height ?? window.innerHeight,
    );
    useEffect(() => {
      const onResize = () => {
        setViewportHeight(window.visualViewport?.height ?? window.innerHeight);
      };
      // visualViewport 対応ブラウザ
      window.visualViewport?.addEventListener('resize', onResize);
      // フォールバック
      window.addEventListener('resize', onResize);
      return () => {
        window.visualViewport?.removeEventListener('resize', onResize);
        window.removeEventListener('resize', onResize);
      };
    }, []);

    const scrollTimeoutRef = useRef<NodeJS.Timeout>();
    const loadingTimeoutRef = useRef<NodeJS.Timeout>();
    const abortControllerRef = useRef<AbortController>();

    useImperativeHandle(ref, () => ({
      scrollToChatById: (chatId: number) => {
        const el = chatRefs.current[chatId];
        if (el) {
          scrollManager.addScrollAction(
            () => {
              el.scrollIntoView({ block: 'center' });
            },
            ScrollPriority.USER_MANUAL,
            {
              description: `scrollToChatById-${chatId}`,
              force: true,
            },
          );
        }
      },
      scrollToUnreadOrBottom: (firstUnreadChatId?: number | null) => {
        if (isLoadingMoreChats.current) {
          setTimeout(() => {
            if (isLoadingMoreChats.current) {
              return;
            }

            if (hasUserScrolledUpRef.current) {
              return;
            }

            hasUserScrolledUpRef.current = false;

            if (firstUnreadChatId) {
              cancelScrollActions(scrollManager, firstUnreadChatId);
            }

            scrollManager.addScrollAction(
              () => {
                if (!firstUnreadChatId) {
                  scrollToBottom();
                  return;
                }

                const el = chatRefs.current[firstUnreadChatId];
                if (!el || !chatContainerRef.current) {
                  return;
                }

                const scrollAction = createScrollToUnreadAction(el, chatContainerRef.current);
                scrollAction();
              },
              ScrollPriority.USER_MANUAL,
              {
                description: `externalScrollToUnread-${firstUnreadChatId || 'bottom'}`,
                force: true,
              },
            );
          }, TIMING_CONFIG.INFINITE_SCROLL_BUFFER);
          return;
        }

        if (hasUserScrolledUpRef.current) {
          return;
        }

        if (!firstUnreadChatId) {
          scrollToBottom();
          return;
        }

        const el = chatRefs.current[firstUnreadChatId];
        if (!el) {
          return;
        }

        hasUserScrolledUpRef.current = false;

        cancelScrollActions(scrollManager, firstUnreadChatId);

        scrollManager.addScrollAction(
          () => {
            const chatContainer = chatContainerRef.current;
            if (!chatContainer) {
              return;
            }

            const scrollAction = createScrollToUnreadAction(el, chatContainer);
            scrollAction();
          },
          ScrollPriority.USER_MANUAL,
          {
            description: `scrollToUnread-${firstUnreadChatId}`,
            force: true,
          },
        );
      },
      editPinnedChat: (pinnedChat: PinnedChat) => {
        const chat = chatGroups
          .flatMap((group) => group.chats)
          .find((chat) => chat.id === pinnedChat.id);
        if (chat) {
          handleChatEditClick(chat);
        }
      },
      deletePinnedChat: (pinnedChat: PinnedChat) => {
        const chat = chatGroups
          .flatMap((group) => group.chats)
          .find((chat) => chat.id === pinnedChat.id);
        if (chat) {
          handleChatDeleteClick(chat);
        }
      },
      reportPinnedChat: (pinnedChat: PinnedChat) => {
        handleChatReportClick(pinnedChat as Chat);
      },
      getChatMenuPermission: (chat: Chat) => getChatMenuPermission(chat),
    }));

    useEffect(() => {
      if (onFooterHeightChange) {
        onFooterHeightChange();
      }

      if (shouldAutoScrollAfterFooter.current) {
        scrollManager.addScrollAction(
          () => {
            return new Promise<void>((resolve) => {
              const adjustScroll = () => {
                scrollToBottom();

                requestAnimationFrame(() => {
                  const el = chatContainerRef.current;
                  if (!el) {
                    shouldAutoScrollAfterFooter.current = false;
                    resolve();
                    return;
                  }

                  const currentDistance = el.scrollHeight - el.scrollTop - el.clientHeight;
                  if (currentDistance > SCROLL_CONFIG.FOOTER_ADJUSTMENT_THRESHOLD) {
                    adjustScroll();
                  } else {
                    shouldAutoScrollAfterFooter.current = false;
                    resolve();
                  }
                });
              };

              adjustScroll();
            });
          },
          ScrollPriority.SEND_AUTO_SCROLL,
          { description: 'footerHeightAdjustment' },
        );
      }
    }, [onFooterHeightChange, footerRef?.current?.clientHeight, scrollToBottom, scrollManager]);

    useEffect(() => {
      const fetchChannelMembers = async () => {
        if (isFileDownloading) {
          return;
        }
        const res = await channelMemberApiClient.index(channelCode, { page: 'all' });
        handleApiResponse(res, (data) => {
          setChannelMembers(data.data.members);
        });
      };

      if (isLoggedIn && channelCode !== '' && channelCode !== null) {
        fetchChannelMembers();
      }

      setIsActionMenuSpOpen(false);
      setCurrentChatIdSp(undefined);
      forceUpdate();
      prevChannelCodeRef.current = channelCode;
    }, [channelCode, isLoggedIn, isFileDownloading]);

    useEffect(() => {
      const loadDraft = () => {
        if (currentUserType && currentUserId) {
          loadChatDraft(channelCode, currentUserType, currentUserId).then((draft) => {
            if (draft) {
              setCreatingMessage(draft.message);
              setCreatingAttachments(draft.attachments ?? []);
            }
          });
        }
      };

      if (prevChannelCode.current !== undefined && prevChannelCode.current !== channelCode) {
        isInitialRender.current = true;
      }

      loadDraft();

      const onVisible = () => {
        if (document.visibilityState === 'visible') {
          loadDraft();
        }
      };

      document.addEventListener('visibilitychange', onVisible);
      prevChannelCode.current = channelCode;

      return () => {
        document.removeEventListener('visibilitychange', onVisible);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [channelCode]);

    useDebouncedEffect(
      () => {
        if (editingChatId !== null) {
          return;
        }
        saveChatDraft(
          channelCode,
          creatingMessage,
          creatingAttachments,
          currentUserType,
          currentUserId,
        );
      },
      [creatingMessage, creatingAttachments],
      500,
    );

    useLayoutEffect(() => {
      const chatContainer = chatContainerRef.current;
      if (!chatContainer) {
        return;
      }

      const hasChats = chatGroups?.some((group) => group.chats && group.chats.length > 0) || false;

      const performScrollAction = () => {
        try {
          if (scrollManager.isScrollInProgress()) {
            const currentAction = scrollManager.getCurrentAction();
            if (currentAction && currentAction.priority <= ScrollPriority.INFINITE_RESTORE) {
              return;
            }
          }

          // Infinite scroll restoration takes highest precedence
          if (isLoadingMoreChats.current && hasChats) {
            const currentChatCount = allChats.length;
            const hasNewChats = currentChatCount > prevChatCountBeforeFetch.current;

            if (!hasNewChats) {
              return;
            }

            // Clear the safety timeout since we successfully got new chats
            if (loadingTimeoutRef.current) {
              clearTimeout(loadingTimeoutRef.current);
            }

            scrollManager.addScrollAction(
              () => {
                return new Promise<void>((resolve) => {
                  requestAnimationFrame(() => {
                    scrollManager.cancelAction(`initialChatUnreadScroll-${initialChatId || 'any'}`);
                    scrollManager.cancelAction(`externalScrollToUnread-${initialChatId || 'any'}`);
                    scrollManager.cancelAction(`initialChatScroll-${initialChatId || 'any'}`);

                    restoreScrollHeight();
                    requestAnimationFrame(() => {
                      isLoadingMoreChats.current = false;
                      setShowLoadingIndicator(false);
                      resolve();
                    });
                  });
                });
              },
              ScrollPriority.INFINITE_RESTORE,
              { description: 'infiniteScrollRestore' },
            );
            return;
          }

          // Initial render bottom scroll
          if (isInitialRender.current && hasChats && !initialChatId) {
            scrollManager.addScrollAction(
              () => {
                scrollToBottom();
                isInitialRender.current = false;
              },
              ScrollPriority.GENERAL_AUTO_SCROLL,
              { description: 'initialRenderBottomScroll' },
            );
            return;
          }

          // Initial render with specific chat
          if (isInitialRender.current && initialChatId) {
            const el = chatRefs.current[initialChatId];
            if (!el) {
              return;
            }

            if (hasUserScrolledUpRef.current) {
              return;
            }

            scrollManager.cancelAction(`initialChatUnreadScroll-${initialChatId}`);
            scrollManager.cancelAction(`externalScrollToUnread-${initialChatId}`);

            scrollManager.addScrollAction(
              () => {
                el.scrollIntoView({ block: 'center' });
                if (pinnedChat?.id !== initialChatId && firstUnreadChatId !== initialChatId) {
                  el.classList.add('chat-highlight');
                }
                isInitialRender.current = false;
              },
              ScrollPriority.UNREAD_NAVIGATION,
              { description: `initialChatScroll-${initialChatId}` },
            );
            return;
          }

          // Auto-scroll for sender's messages only
          const shouldAutoScroll =
            isNearBottom() &&
            hasChats &&
            !initialChatId &&
            !isInfiniteScrollDownRef.current &&
            !isSendingRef.current &&
            !isLoadingMoreChats.current &&
            !hasUserScrolledUpRef.current;

          if (!shouldAutoScroll) {
            return;
          }

          const isSenderMessage =
            newestChat &&
            newestChat.user_id === currentUserId &&
            newestChat.user_type === currentUserType;

          if (!isSenderMessage) {
            return;
          }

          scrollManager.addScrollAction(
            () => scrollToBottom(),
            ScrollPriority.GENERAL_AUTO_SCROLL,
            { description: 'senderMessageAutoScroll' },
          );
        } catch (error) {
          logger.error('Error in performScrollAction:', error);
        }
      };

      performScrollAction();
    }, [
      chatGroups,
      allChats.length,
      isNearBottom,
      scrollToBottom,
      restoreScrollHeight,
      initialChatId,
      pinnedChat?.id,
      firstUnreadChatId,
      currentUserId,
      currentUserType,
      newestChat,
      scrollManager,
    ]);

    useEffect(() => {
      if (
        !initialChatId ||
        initialChatId === prevScrollToId.current ||
        isInitialRender.current ||
        isLoadingMoreChats.current
      ) {
        return;
      }

      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      const el = chatRefs.current[initialChatId];
      if (!el) {
        return;
      }

      // First unread chat scroll with infinite scroll guard - route through scroll manager
      if (firstUnreadChatId === initialChatId) {
        scrollTimeoutRef.current = setTimeout(() => {
          // Don't execute if scroll action in progress or infinite scroll started
          if (scrollManager.isScrollInProgress() || isLoadingMoreChats.current) {
            return;
          }

          if (hasUserScrolledUpRef.current) {
            return;
          }

          cancelScrollActions(scrollManager, initialChatId);

          scrollManager.addScrollAction(
            () => {
              const chatContainer = chatContainerRef.current;
              if (!chatContainer) {
                return;
              }

              const scrollAction = createScrollToUnreadAction(el, chatContainer);
              scrollAction();

              prevScrollToId.current = initialChatId;
              if (pinnedChat?.id !== initialChatId && firstUnreadChatId !== initialChatId) {
                el.classList.add('chat-highlight');
              }
            },
            ScrollPriority.UNREAD_NAVIGATION,
            { description: `initialChatUnreadScroll-${initialChatId}` },
          );
        }, SCROLL_CONFIG.DELAY_MS);
        return;
      }

      if (isLoadingMoreChats.current) {
        return;
      }

      if (hasUserScrolledUpRef.current) {
        return;
      }

      cancelScrollActions(scrollManager, initialChatId);

      scrollManager.addScrollAction(
        () => {
          el.scrollIntoView({ block: 'center' });
          prevScrollToId.current = initialChatId;
          if (pinnedChat?.id !== initialChatId && firstUnreadChatId !== initialChatId) {
            el.classList.add('chat-highlight');
          }
        },
        ScrollPriority.UNREAD_NAVIGATION,
        { description: `initialChatScroll-${initialChatId}` },
      );
    }, [initialChatId, chatGroups, firstUnreadChatId, pinnedChat, scrollManager]);

    useEffect(() => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const sortedChats = [...allChats].sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      );

      const MAX_CONCURRENT_REQUESTS = LINK_PREVIEW_CONFIG.MAX_CONCURRENT_REQUESTS;
      const BATCH_DELAY_MS = LINK_PREVIEW_CONFIG.BATCH_DELAY_MS;

      abortControllerRef.current = new AbortController();
      const currentAbortController = abortControllerRef.current;

      const processChatPreview = async (chat: Chat) => {
        if (currentAbortController.signal.aborted || isLoadingMoreChats.current) {
          return;
        }

        const key = String(chat.id);
        if (key in linkPreviewCache) {
          return;
        }

        // Skip fetching if preview is hidden
        if (chat.is_link_preview_hidden) {
          if (!currentAbortController.signal.aborted && !isLoadingMoreChats.current) {
            setLinkPreviewCache((prev) => ({ ...prev, [key]: null }));
          }
          return;
        }

        const firstUrl = extractFirstUrl(chat.message);
        if (!firstUrl) {
          if (!currentAbortController.signal.aborted && !isLoadingMoreChats.current) {
            setLinkPreviewCache((prev) => ({ ...prev, [key]: null }));
          }
          return;
        }

        try {
          const preview = await fetchLinkPreview(firstUrl);
          if (!currentAbortController.signal.aborted && !isLoadingMoreChats.current) {
            setLinkPreviewCache((prev) => ({ ...prev, [key]: preview }));
          }
        } catch (error) {
          logger.error(error);
          if (!currentAbortController.signal.aborted && !isLoadingMoreChats.current) {
            setLinkPreviewCache((prev) => ({ ...prev, [key]: null }));
          }
        }
      };

      const isChatVisible = (chatId: number) => {
        const chatElement = chatRefs.current[chatId];
        if (!chatElement || !chatContainerRef.current) {
          return false;
        }

        const container = chatContainerRef.current;
        const containerRect = container.getBoundingClientRect();
        const chatRect = chatElement.getBoundingClientRect();

        return chatRect.top >= containerRect.top && chatRect.bottom <= containerRect.bottom;
      };

      const processChats = async () => {
        const visibleChats = sortedChats.filter((chat) => isChatVisible(chat.id));
        const nonVisibleChats = sortedChats.filter((chat) => !isChatVisible(chat.id));
        const prioritizedChats = [...visibleChats, ...nonVisibleChats];

        for (let i = 0; i < prioritizedChats.length; i += MAX_CONCURRENT_REQUESTS) {
          if (currentAbortController.signal.aborted) {
            break;
          }

          const batch = prioritizedChats.slice(i, i + MAX_CONCURRENT_REQUESTS);
          await Promise.all(batch.map(processChatPreview));

          if (i + MAX_CONCURRENT_REQUESTS < prioritizedChats.length) {
            await new Promise((resolve) => setTimeout(resolve, BATCH_DELAY_MS));
          }
        }
      };

      processChats();

      return () => {
        currentAbortController.abort();
        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chatGroups, channelCode, allChats]);

    const handleCreatingMessageChange = (text: string) => setCreatingMessage(text);
    const handleCreatingAttachmentsChange = (attachments: Attachment[]) => {
      setCreatingAttachments(attachments);
    };
    const handleEditingMessageChange = (text: string) => setEditingMessage(text);
    const handleEditingAttachmentsChange = (attachments: Attachment[]) => {
      setEditingAttachments(attachments);
    };

    const handleChatDeleteClick = (chat: Chat) => {
      setDeletingChatId(chat.id);
      setIsDeleteModalOpen(true);
    };

    const handleChatReportClick = (chat: Chat) => {
      setReportingChatId(chat.id);
      setIsReportModalOpen(true);
    };

    const handleReportSubmit = async (reason: ChatReportReasonEnum) => {
      if (!reportingChatId) {
        return;
      }

      const params: ChatReportRequest = { reason };
      const response = await chatApiClient.report(channelCode, reportingChatId, params);

      handleApiResponse<ChatReportResponse, ChatReportErrorResponse>(
        response,
        () => {
          setIsReportModalOpen(false);
          setReportingChatId(null);
          toast(TOAST_MESSAGE.SEND);
        },
        (errors) => {
          logger.error('Chat report failed:', errors);
        },
      );
    };

    const handleReportCancel = () => {
      setIsReportModalOpen(false);
      setReportingChatId(null);
    };

    const handleToggleLinkPreview = (chatId: number, hidePreview: boolean) => {
      if (hidePreview) {
        setHidingPreviewChatId(chatId);
        setIsHidePreviewModalOpen(true);
      } else {
        performToggleLinkPreview(chatId, hidePreview);
      }
    };

    const performToggleLinkPreview = async (chatId: number, hidePreview: boolean) => {
      const result = await chatApiClient.toggleLinkPreview(channelCode, chatId, hidePreview);
      handleApiResponse(
        result,
        () => {
          mutateChats();
        },
        (error) => {
          logger.error('Error toggling link preview:', error);
        },
      );
    };

    const handleConfirmHidePreview = async () => {
      if (hidingPreviewChatId === null) {
        return;
      }

      setLinkPreviewCache((prev) => {
        const key = String(hidingPreviewChatId);
        return { ...prev, [key]: null };
      });

      setIsHidePreviewModalOpen(false);
      const chatIdToHide = hidingPreviewChatId;
      setHidingPreviewChatId(null);

      const result = await chatApiClient.toggleLinkPreview(channelCode, chatIdToHide, true);
      handleApiResponse(
        result,
        () => {
          mutateChats();
        },
        (error) => {
          logger.error('リンクプレビューの切り替え中にエラーが発生しました:', error);
          const chat = allChats.find((c) => c.id === chatIdToHide);
          if (!chat) {
            return;
          }

          const firstUrl = extractFirstUrl(chat.message);
          if (!firstUrl) {
            return;
          }

          fetchLinkPreview(firstUrl).then((preview) => {
            setLinkPreviewCache((prev) => ({
              ...prev,
              [String(chatIdToHide)]: preview,
            }));
          });
        },
      );
    };

    const handleCancelHidePreview = () => {
      setIsHidePreviewModalOpen(false);
      setHidingPreviewChatId(null);
    };

    const handleConfirmDelete = async () => {
      if (deletingChatId === null) {
        return;
      }
      const success = await deleteMessage(deletingChatId);
      if (success) {
        mutateChats();
      }
      setIsDeleteModalOpen(false);
      setDeletingChatId(null);
    };

    const handleCancelDelete = () => {
      tiptapEditorRef.current?.resetErrorMessage();
      setIsDeleteModalOpen(false);
      setDeletingChatId(null);
    };

    const [isSending, setIsSending] = useState(false);
    const [autoScrollOnNew, setAutoScrollOnNew] = useState(false);
    const sendCountRef = useRef(0);
    const pendingSendsRef = useRef<Set<number>>(new Set());
    const sendIdCounter = useRef(0);
    const sendPositionMap = useRef<Map<number, boolean>>(new Map());

    useEffect(() => {
      if (isSending && autoScrollOnNew) {
        shouldAutoScrollAfterFooter.current = true;

        const isMultipleSends = pendingSendsRef.current.size > 1;
        const scrollDelay = isMultipleSends
          ? TIMING_CONFIG.SEND_SCROLL_DELAY_MULTIPLE
          : TIMING_CONFIG.SEND_SCROLL_DELAY_SINGLE;

        scrollManager.addScrollAction(
          () => {
            return new Promise<void>((resolve) => {
              setTimeout(() => {
                scrollToBottom();

                // Verification with limited attempts
                let attempts = 0;
                const verifyAndCorrect = () => {
                  const el = chatContainerRef.current;
                  if (!el || attempts >= SCROLL_CONFIG.MAX_VERIFICATION_ATTEMPTS) {
                    resolve();
                    return;
                  }

                  const currentDistance = el.scrollHeight - el.scrollTop - el.clientHeight;
                  if (currentDistance <= SCROLL_CONFIG.SCROLL_VERIFICATION_THRESHOLD) {
                    resolve();
                    return;
                  }

                  attempts++;
                  scrollToBottom();
                  requestAnimationFrame(verifyAndCorrect);
                };

                requestAnimationFrame(verifyAndCorrect);
              }, scrollDelay);
            });
          },
          ScrollPriority.SEND_AUTO_SCROLL,
          { description: `sendAutoScroll-${sendCountRef.current}` },
        );

        setAutoScrollOnNew(false);
        setIsSending(false);

        sendCountRef.current = Math.max(0, sendCountRef.current - 1);

        if (pendingSendsRef.current.size === 0) {
          isSendingRef.current = false;
        }
      } else if (isSending && !autoScrollOnNew) {
        setIsSending(false);

        sendCountRef.current = Math.max(0, sendCountRef.current - 1);

        if (pendingSendsRef.current.size === 0) {
          isSendingRef.current = false;
        }
      }
    }, [chatGroups, isSending, autoScrollOnNew, scrollToBottom, scrollManager]);

    const handleSendClick = async () => {
      const userAtBottom = isNearBottom();
      sendCountRef.current += 1;
      sendIdCounter.current += 1;
      const currentSendId = sendIdCounter.current;

      pendingSendsRef.current.add(currentSendId);
      sendPositionMap.current.set(currentSendId, userAtBottom);
      isSendingRef.current = true;

      const shouldAutoScroll = sendPositionMap.current.get(currentSendId) || false;
      setAutoScrollOnNew(shouldAutoScroll);

      try {
        if (channelCode && currentUserType != null && currentUserId != null) {
          await deleteChatDraft(channelCode, currentUserType, currentUserId);
        }

        if (editingChatId !== null) {
          setLinkPreviewCache((prev) => {
            const newCache = { ...prev };
            delete newCache[String(editingChatId)];
            return newCache;
          });
          setEditingChatId(null);
          setEditingMessage('');
          setEditingAttachments([]);
        }

        setCreatingMessage('');
        setCreatingAttachments([]);
        setIsSending(true);

        await onSend?.();
      } catch (error) {
        logger.error('Error sending chat:', error);
        setIsSending(false);
        setAutoScrollOnNew(false);
        sendCountRef.current = Math.max(0, sendCountRef.current - 1);

        if (pendingSendsRef.current.size <= 1) {
          isSendingRef.current = false;
        }
      } finally {
        pendingSendsRef.current.delete(currentSendId);
        sendPositionMap.current.delete(currentSendId);

        if (pendingSendsRef.current.size === 0) {
          isSendingRef.current = false;
        }
      }
    };

    const handleChatEditClick = useCallback(
      (chat: Chat) => {
        tiptapEditorRef.current?.resetErrorMessage();
        setEditingChatId(chat.id);
        setEditingMessage(chat.message);
        setEditingAttachments(
          (chat.attachments ?? []).map((chatAttachment) => ({
            name: chatAttachment.file_name,
            type: chatAttachment.extension,
            size: 0,
            blob: new Blob(),
            chatAttachment: chatAttachment,
          })),
        );
        setIsAnonymous(chat.is_anonymous);
      },
      [setEditingChatId],
    );

    const handleScroll = useCallback(
      (event: Event) => {
        try {
          if (
            event.target !== chatContainerRef.current ||
            isLoadingMoreChats.current ||
            isLoadingNewerChats.current ||
            scrollManager.isScrollInProgress()
          ) {
            return;
          }

          const atBottom = isNearBottom();
          wasAtBottomRef.current = atBottom;

          // Reset infinite scroll flag if user scrolls back to bottom
          if (atBottom && hasUserScrolledUpRef.current) {
            hasUserScrolledUpRef.current = false;
          }

          const mentionPopup = document.querySelector('div[style*="z-index: 99999"]');
          if (mentionPopup) {
            return;
          }

          updateWasAtBottom();

          const chatContainer = chatContainerRef.current;
          if (!chatContainer) {
            return;
          }

          const { scrollTop, scrollHeight, clientHeight } = chatContainer;

          // Handle infinite scroll up (top)
          const shouldTriggerInfiniteScrollUp =
            chatContainer.scrollTop === 0 && hasMoreChats && !isLoadingMoreChats.current;

          if (shouldTriggerInfiniteScrollUp) {
            isLoadingMoreChats.current = true;
            setShowLoadingIndicator(true);
            const currentChatCount = allChats.length;
            prevChatCountBeforeFetch.current = currentChatCount;
            hasUserScrolledUpRef.current = true; // Mark that user has scrolled up for infinite scroll
            rememberScrollHeight();
            forceUpdate();

            // Safety timeout to reset loading state if fetch takes too long
            if (loadingTimeoutRef.current) {
              clearTimeout(loadingTimeoutRef.current);
            }
            loadingTimeoutRef.current = setTimeout(() => {
              if (isLoadingMoreChats.current) {
                isLoadingMoreChats.current = false;
                setShowLoadingIndicator(false);
              }
            }, TIMING_CONFIG.SAFETY_TIMEOUT);

            void fetchMoreChats();
            return;
          }

          // Handle infinite scroll down (bottom)
          const isVeryNearBottom =
            scrollTop + clientHeight >= scrollHeight - SCROLL_CONFIG.NEAR_BOTTOM_THRESHOLD;

          const shouldTriggerInfiniteScrollDown =
            isVeryNearBottom &&
            fetchMoreNewerChats &&
            hasMoreNewerChats &&
            !isLoadingNewerChats.current;

          if (!shouldTriggerInfiniteScrollDown) {
            return;
          }

          isLoadingNewerChats.current = true;
          isInfiniteScrollDownRef.current = true;
          hasUserScrolledUpRef.current = true;
          forceUpdate();

          rememberScrollHeight();

          void fetchMoreNewerChats()
            .then(() => {
              if (chatContainer) {
                wasAtBottomRef.current = false;
                restoreScrollHeight();
              }
            })
            .finally(() => {
              isLoadingNewerChats.current = false;
              setTimeout(() => {
                isInfiniteScrollDownRef.current = false;
              }, TIMING_CONFIG.INFINITE_SCROLL_DOWN_RESET);
              forceUpdate();
            });
        } catch (error) {
          logger.error('Error in handleScroll:', error);
        }
      },
      [
        fetchMoreChats,
        hasMoreChats,
        fetchMoreNewerChats,
        hasMoreNewerChats,
        isNearBottom,
        updateWasAtBottom,
        rememberScrollHeight,
        restoreScrollHeight,
        scrollManager,
        allChats,
      ],
    );

    const handlePinnedChatLongPress = () => {
      tiptapEditorRef.current?.blur();
      setTimeout(() => {
        tiptapEditorRef.current?.blur();
        setIsActionMenuSpOpen(true);
      }, 0);
    };
    const handlePinnedChatMenuClose = () => {
      setIsActionMenuSpOpen(false);
    };

    const handleDragEnter: React.DragEventHandler<HTMLDivElement> = (e) => {
      if (!allowFileAttachment || !isLoggedIn || !hasFiles(e.dataTransfer)) {
        return;
      }
      e.preventDefault();
      dragCounter.current += 1;
      setIsDragging(true);
    };
    const handleDragLeave: React.DragEventHandler<HTMLDivElement> = (e) => {
      if (!isLoggedIn || !allowFileAttachment) {
        return;
      }
      e.preventDefault();

      if (dragCounter.current > 0) {
        dragCounter.current -= 1;
      }

      if (dragCounter.current === 0) {
        setIsDragging(false);
      }
    };
    const handleDragOver: React.DragEventHandler<HTMLDivElement> = (e) => {
      e.preventDefault();
    };
    const handleDropOnContainer: React.DragEventHandler<HTMLDivElement> = (e) => {
      e.preventDefault();
      dragCounter.current = 0;
      setIsDragging(false);

      if (!isLoggedIn || !allowFileAttachment) {
        tiptapEditorRef.current?.setAttachmentError(ERROR_MESSAGES.FILE.ATTACHMENT_NOT_ALLOWED);
        return;
      }

      const files = e.dataTransfer.files;
      if (files && files.length > 0) {
        tiptapEditorRef.current?.selectFiles(files);
      }
    };

    const [isMentionScrolling, setIsMentionScrolling] = useState(false);

    useEffect(() => {
      const handleMentionScrollStart = () => setIsMentionScrolling(true);
      const handleMentionScrollEnd = () => setIsMentionScrolling(false);

      document.addEventListener('mentionScrollStart', handleMentionScrollStart);
      document.addEventListener('mentionScrollEnd', handleMentionScrollEnd);

      return () => {
        document.removeEventListener('mentionScrollStart', handleMentionScrollStart);
        document.removeEventListener('mentionScrollEnd', handleMentionScrollEnd);
      };
    }, []);

    useEffect(() => {
      const chatContainer = chatContainerRef.current;
      if (!chatContainer) {
        return;
      }

      const handleTouch = (e: TouchEvent) => {
        const target = e.target as HTMLElement;
        const mentionPopup = document.querySelector('div[style*="z-index: 99999"]');

        if (!mentionPopup || !chatContainer.contains(target) || !mentionPopup.contains(target)) {
          return;
        }

        if (isMentionScrolling) {
          e.stopPropagation();
        }
      };

      let scrollTimeoutId: NodeJS.Timeout;
      let lastScrollTime = 0;
      const SCROLL_DEBOUNCE_MS = SCROLL_CONFIG.DEBOUNCE_MS;
      const SCROLL_THROTTLE_MS = SCROLL_CONFIG.THROTTLE_MS;

      const debouncedHandleScroll = (event: Event) => {
        clearTimeout(scrollTimeoutId);

        const now = Date.now();
        if (now - lastScrollTime < SCROLL_THROTTLE_MS) {
          scrollTimeoutId = setTimeout(() => handleScroll(event), SCROLL_DEBOUNCE_MS);
          return;
        }

        lastScrollTime = now;

        const chatContainer = chatContainerRef.current;
        if (!chatContainer || isLoadingMoreChats.current || isLoadingNewerChats.current) {
          scrollTimeoutId = setTimeout(() => handleScroll(event), SCROLL_DEBOUNCE_MS);
          return;
        }

        const { scrollTop, scrollHeight, clientHeight } = chatContainer;

        // Top scroll - immediate infinite scroll trigger
        if (scrollTop === 0 && hasMoreChats) {
          handleScroll(event);
          return;
        }

        // Bottom scroll - immediate infinite scroll trigger
        const isVeryNearBottom =
          scrollTop + clientHeight >= scrollHeight - SCROLL_CONFIG.NEAR_BOTTOM_THRESHOLD;
        if (isVeryNearBottom && fetchMoreNewerChats && hasMoreNewerChats) {
          handleScroll(event);
          return;
        }

        scrollTimeoutId = setTimeout(() => handleScroll(event), SCROLL_DEBOUNCE_MS);
      };

      const handleScrollEvent = (e: Event) => {
        if (e.target !== chatContainer) {
          return;
        }

        const mentionPopup = document.querySelector('div[style*="z-index: 99999"]');
        if (mentionPopup || isMentionScrolling) {
          return;
        }

        debouncedHandleScroll(e);
      };

      const interceptTouch = (e: TouchEvent) => {
        const mentionPopup = document.querySelector('div[style*="z-index: 99999"]');
        const target = e.target as HTMLElement;

        if (!mentionPopup || !isMentionScrolling) {
          return;
        }

        if (!chatContainer.contains(target) || mentionPopup.contains(target)) {
          return;
        }

        e.preventDefault();
        e.stopPropagation();
      };

      chatContainer.addEventListener('scroll', handleScrollEvent, { passive: true });
      chatContainer.addEventListener('touchmove', handleTouch, { passive: false });
      document.body.addEventListener('touchmove', interceptTouch, {
        passive: false,
        capture: true,
      });

      return () => {
        clearTimeout(scrollTimeoutId);
        if (loadingTimeoutRef.current) {
          clearTimeout(loadingTimeoutRef.current);
        }
        chatContainer.removeEventListener('scroll', handleScrollEvent);
        chatContainer.removeEventListener('touchmove', handleTouch);
        document.body.removeEventListener('touchmove', interceptTouch, true);
      };
    }, [handleScroll, isMentionScrolling, hasMoreChats, fetchMoreNewerChats, hasMoreNewerChats]);

    const getChatMenuPermission = useCallback(
      (chat: Chat): ChatMenuPermission => {
        if (!isLoggedIn) {
          return { canShowKebabMenu: true, canEdit: false, canDelete: false, canReport: true };
        }

        const isTenantAdmin = profile?.isTenant && profile?.tenantProfile?.user?.isAdmin;
        const isMyPost = chat.user_id === currentUserId && chat.user_type === currentUserType;

        const canEdit = (isTenantAdmin && isMyPost) || (!isTenantAdmin && isMyPost && canPost);
        const canDelete = isTenantAdmin || (!isTenantAdmin && isMyPost && canPost);
        const canReport = !isMyPost;
        const canShowKebabMenu = canEdit || canDelete || canReport;

        return { canShowKebabMenu, canEdit, canDelete, canReport };
      },
      [isLoggedIn, profile, currentUserId, currentUserType, canPost],
    );

    const shouldShowUnreadLine = useCallback(
      (chatId: number) => {
        return chatId === firstUnreadChatId;
      },
      [firstUnreadChatId],
    );

    const prevChatCountRef = useRef<number>(totalChatCount);
    // Total chat count auto-scroll (for sender messages only)
    useEffect(() => {
      const shouldAutoScroll =
        totalChatCount > prevChatCountRef.current &&
        isNearBottom() &&
        !isInfiniteScrollDownRef.current &&
        !isLoadingMoreChats.current &&
        !hasUserScrolledUpRef.current;

      if (!shouldAutoScroll) {
        prevChatCountRef.current = totalChatCount;
        return;
      }

      const isSenderMessage =
        newestChat &&
        newestChat.user_id === currentUserId &&
        newestChat.user_type === currentUserType;

      if (!isSenderMessage) {
        prevChatCountRef.current = totalChatCount;
        return;
      }

      scrollManager.addScrollAction(() => scrollToBottom(), ScrollPriority.GENERAL_AUTO_SCROLL, {
        description: 'totalChatCountAutoScroll',
      });

      prevChatCountRef.current = totalChatCount;
    }, [
      totalChatCount,
      isNearBottom,
      scrollToBottom,
      currentUserId,
      currentUserType,
      newestChat,
      scrollManager,
    ]);

    const isMobile = useIsMobile();
    const isInitFocus = isMobile ? false : isLoggedIn; // スマホなら絶対False、PCならログイン済みならTrue、未ログインならFalse

    useIOSKeyboard();

    const [showUnreadLines, setShowUnreadLines] = useState(true);
    const prevChannelCodeRef = useRef<string>();

    useEffect(() => {
      if (prevChannelCodeRef.current && prevChannelCodeRef.current !== channelCode) {
        setShowUnreadLines(false);
        hasUserScrolledUpRef.current = false; // Reset flag when changing channels

        setTimeout(() => {
          const chatContainer = chatContainerRef.current;
          if (chatContainer) {
            chatContainer.scrollTop = 0;
          }
        }, TIMING_CONFIG.SCROLL_RESET_DELAY);

        setTimeout(() => {
          setShowUnreadLines(true);
        }, TIMING_CONFIG.UNREAD_LINE_RESET_DELAY);
      }

      prevChannelCodeRef.current = channelCode;
    }, [channelCode]);

    useEffect(() => {
      const handleVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
          setShowUnreadLines(false);
          setTimeout(() => {
            setShowUnreadLines(true);
          }, TIMING_CONFIG.VISIBILITY_RESET_DELAY);
        }
      };

      const handlePageShow = (event: PageTransitionEvent) => {
        if (event.persisted) {
          setShowUnreadLines(false);
          setTimeout(() => {
            setShowUnreadLines(true);
          }, TIMING_CONFIG.VISIBILITY_RESET_DELAY);
        }
      };

      const handleBeforeUnload = () => {
        setShowUnreadLines(false);
      };

      document.addEventListener('visibilitychange', handleVisibilityChange);
      window.addEventListener('pageshow', handlePageShow);
      window.addEventListener('beforeunload', handleBeforeUnload);

      return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        window.removeEventListener('pageshow', handlePageShow);
        window.removeEventListener('beforeunload', handleBeforeUnload);
      };
    }, []);

    useEffect(() => {
      const chatContainer = chatContainerRef.current;
      if (!chatContainer) {
        return;
      }

      const handleScrollToUnread = () => {
        if (!firstUnreadChatId || !showUnreadLines) {
          return;
        }

        const unreadElement = chatRefs.current[firstUnreadChatId];
        if (!unreadElement) {
          return;
        }

        const containerRect = chatContainer.getBoundingClientRect();
        const unreadRect = unreadElement.getBoundingClientRect();

        if (unreadRect.top < containerRect.top + SCROLL_CONFIG.UNREAD_VISIBILITY_THRESHOLD) {
          setShowUnreadLines(false);
        }
      };

      chatContainer.addEventListener('scroll', handleScrollToUnread);
      return () => {
        chatContainer.removeEventListener('scroll', handleScrollToUnread);
      };
    }, [firstUnreadChatId, showUnreadLines]);

    // useEffect for global drag cleanup
    useEffect(() => {
      const handleGlobalDragEnd = () => {
        if (isDragging) {
          setIsDragging(false);
          dragCounter.current = 0;
        }
      };

      const handleGlobalDrop = () => {
        setIsDragging(false);
        dragCounter.current = 0;
      };

      const handleMouseLeave = () => {
        if (isDragging) {
          setTimeout(() => {
            setIsDragging(false);
            dragCounter.current = 0;
          }, TIMING_CONFIG.VISIBILITY_RESET_DELAY);
        }
      };

      document.addEventListener('dragend', handleGlobalDragEnd);
      document.addEventListener('drop', handleGlobalDrop);
      document.addEventListener('mouseleave', handleMouseLeave);
      window.addEventListener('blur', handleGlobalDragEnd);

      return () => {
        document.removeEventListener('dragend', handleGlobalDragEnd);
        document.removeEventListener('drop', handleGlobalDrop);
        document.removeEventListener('mouseleave', handleMouseLeave);
        window.removeEventListener('blur', handleGlobalDragEnd);
      };
    }, [isDragging]);

    return (
      <div
        className='flex flex-1 flex-col overflow-hidden'
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDropOnContainer}
      >
        <div
          ref={chatContainerRef}
          id='chat-container'
          className='flex-1 flex-col overflow-auto overscroll-y-none pt-2'
        >
          {showLoadingIndicator && (
            <div className='flex justify-center px-6 py-4'>
              <div className='flex items-center gap-2 text-sm text-gray-500'>
                <div className='size-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600'></div>
              </div>
            </div>
          )}

          {chatGroups.map((group, index) => (
            <div
              key={group.date_label + index}
              className='py-4'
            >
              <div className='border-t border-solid border-t-gray-200'>
                <div className='sticky top-0 z-[8] m-auto mt-[-13px] h-[26px] w-fit'>
                  <time className='rounded-[100px] border border-gray-200 bg-white px-4 py-1 text-xs font-medium'>
                    {group.date_label}
                  </time>
                </div>
                <div className='pt-1.5'>
                  {group.chats.map((chat) => (
                    <ChatMessage
                      key={chat.id}
                      contextData={{
                        chat,
                        channelCode,
                        channelVisibility,
                        pinnedChat,
                        linkPreviewCache,
                      }}
                      userState={{
                        isLoggedIn,
                        currentUserId,
                        currentUserType,
                      }}
                      uiState={{
                        editingChatId,
                        isActionMenuSpOpen,
                        currentChatIdSp,
                      }}
                      callbacks={{
                        onPinClick,
                        onChatEditClick: handleChatEditClick,
                        onChatDeleteClick: handleChatDeleteClick,
                        onChatReportClick: handleChatReportClick,
                        onPinnedChatMenuClose: handlePinnedChatMenuClose,
                        onSetCurrentChatIdSp: setCurrentChatIdSp,
                        onPinnedChatLongPress: handlePinnedChatLongPress,
                        onToggleLinkPreview: handleToggleLinkPreview,
                      }}
                      shouldShowUnreadLine={shouldShowUnreadLine}
                      getChatMenuPermission={getChatMenuPermission}
                      chatRef={(el) => (chatRefs.current[chat.id] = el)}
                    />
                  ))}
                </div>
              </div>
            </div>
          ))}

          {isLoadingNewerChats.current && (
            <div className='flex justify-center px-6 py-4'>
              <div className='flex items-center gap-2 text-sm text-gray-500'>
                <div className='size-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600'></div>
              </div>
            </div>
          )}

          {!canPost &&
            !isLoggedIn &&
            (isMobile ? (
              <div
                className='absolute inset-0 z-[9999] grid place-content-center
       bg-white/85 lg:fixed lg:bottom-[15px] lg:left-1/2 lg:top-auto
       lg:w-[calc(100%-30px)] lg:-translate-x-1/2 lg:rounded-[4px] lg:border
        lg:border-gray-200 lg:py-6 md:py-4'
              >
                <LoginPrompt />
              </div>
            ) : (
              <div className='inset-x-0 bg-white/85 py-6 xl:left-0 xl:w-full md:py-4'>
                <LoginRecommendOverlay />
              </div>
            ))}
        </div>
        {canPost && (
          <div
            ref={footerRef}
            className={`relative z-10 shrink-0 bg-white px-2 py-3 lg:px-4 lg:pb-5 lg:pt-3 lg:[&_.toolbar]:hidden`}
          >
            <div
              className='touch-pan-y overflow-y-auto overscroll-contain'
              style={{ maxHeight: `${viewportHeight * 0.5}px`, WebkitOverflowScrolling: 'touch' }}
            >
              <CommunityTiptapEditor
                ref={tiptapEditorRef}
                value={editingChatId !== null ? editingMessage : creatingMessage}
                onChange={
                  editingChatId !== null ? handleEditingMessageChange : handleCreatingMessageChange
                }
                onSend={handleSendClick}
                attachments={editingChatId !== null ? editingAttachments : creatingAttachments}
                onAttachmentChange={
                  editingChatId !== null
                    ? handleEditingAttachmentsChange
                    : handleCreatingAttachmentsChange
                }
                enabled={{
                  richText: true,
                  mentions: true,
                  images: true,
                  links: true,
                  tables: false,
                  iframes: false,
                  textStyles: true, // This enables bold, italic, strike
                  lists: true, // This enables bulletList, orderedList
                  codeBlocks: false,
                  quotes: true, // This enables blockquote
                  dividers: false,
                  history: true,
                  dragDrop: false,
                  fileUpload: false,
                  fileSave: false,
                  shortcut: {
                    underline: false,
                    strike: true,
                    heading: false,
                    textAlign: false,
                    blockquote: true,
                  },
                }}
                height='100%'
                width='100%'
                containerHeight='100%'
                channelCode={channelCode}
                channelName={channelName}
                channelMembers={channelMembers}
                channelVisibility={channelVisibility}
                isAnonymous={isAnonymous}
                onChangeIsAnonymous={(isAnonymous: boolean) => {
                  setIsAnonymous(isAnonymous);
                }}
                isEditing={editingChatId !== null}
                editedChatId={editingChatId ?? undefined}
                allowFileAttachment={allowFileAttachment}
                allowAnonymousPost={allowAnonymousPost}
                onCancelEdit={() => {
                  setEditingChatId(null);
                  setEditingMessage('');
                  setEditingAttachments([]);
                  setIsAnonymous(false);
                  tiptapEditorRef.current?.resetErrorMessage();
                }}
                currentUserId={currentUserId}
                currentUserType={currentUserType}
                isInitFocus={isInitFocus}
                isLoggedIn={isLoggedIn}
              />
            </div>
            {!isLoggedIn && (
              <div
                className='absolute inset-0 z-[9999] grid place-content-center
             bg-white/85 lg:fixed lg:bottom-[15px] lg:left-1/2 lg:top-auto
             lg:w-[calc(100%-30px)] lg:-translate-x-1/2 lg:rounded-[4px] lg:border
              lg:border-gray-200 lg:py-6 md:py-4'
              >
                <LoginPrompt />
              </div>
            )}
            {isDeleteModalOpen && (
              <ConfirmMessageDeleteModal
                onConfirm={handleConfirmDelete}
                onClose={handleCancelDelete}
                isDeleting={isDeleting}
              />
            )}
            {isHidePreviewModalOpen && (
              <ConfirmHidePreviewModal
                onConfirm={handleConfirmHidePreview}
                onClose={handleCancelHidePreview}
              />
            )}
          </div>
        )}
        {isDragging && canPost && (
          <div className='pointer-events-none fixed inset-0 z-[1100] flex items-center justify-center bg-black/30'>
            <div className='flex items-center gap-2 rounded-md bg-white px-6 py-4 shadow-lg'>
              <img
                src={IconImage}
                alt='ファイルアップロード用の画像アイコン'
                className='size-5'
              />
              <p className='text-sm font-semibold'>「{channelName}」にファイルをアップロードする</p>
            </div>
          </div>
        )}
        {isReportModalOpen && (
          <ReportModal
            isOpen={isReportModalOpen}
            onClose={handleReportCancel}
            onSubmit={handleReportSubmit}
          />
        )}
      </div>
    );
  },
);

export default ChatBox;
